import { ArrowUpRight } from "lucide-react";
import { FadeUp, Overline, MaskedLines } from "@/components/Reveal";
import { SERVICES, IMG } from "@/data/site";
import { lenisScroll } from "@/lib/scroll";
import CompareSlider from "@/components/CompareSlider";

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <FadeUp>
          <Overline>Services</Overline>
        </FadeUp>
        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-7xl leading-[0.98] tracking-tight">
            <MaskedLines lines={["THE ART OF", "POST-PRODUCTION."]} />
          </h2>
          <FadeUp delay={0.2}>
            <p className="max-w-sm text-sm text-muted font-light leading-relaxed">
              A complete visual post-production partner — from single exposures to
              cinematic film. Where light meets precision.
            </p>
          </FadeUp>
        </div>

        <div className="mt-16 md:mt-24">
          {SERVICES.map((s, i) =>
            s.featured ? (
              <DayToDuskBlock key={s.num} service={s} />
            ) : (
              <FadeUp key={s.num} delay={0.05}>
                <button
                  data-testid={`service-row-${s.num}`}
                  onClick={() => (s.to ? (window.location.href = s.to) : lenisScroll("#contact"))}
                  className="group w-full text-left grid md:grid-cols-12 gap-6 md:gap-8 items-start border-t hairline py-10 md:py-14 px-2 md:px-4 hover:bg-charcoal/70 transition-colors duration-500"
                >
                  <span className="md:col-span-1 font-display text-2xl text-gold/80">{s.num}</span>
                  <span className="md:col-span-5 block">
                    <span className="block font-display font-light text-3xl md:text-4xl leading-tight group-hover:text-gold transition-colors duration-500">
                      {s.title}
                    </span>
                    <span className="block mt-3 text-sm text-muted font-light leading-relaxed max-w-md">
                      {s.desc}
                    </span>
                  </span>
                  <span className="md:col-span-4 grid grid-cols-2 gap-x-6 gap-y-2">
                    {s.items.map((it) => (
                      <span key={it} className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-ivory/60">
                        {it}
                      </span>
                    ))}
                  </span>
                  <span className="md:col-span-2 flex md:justify-end items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-bronze opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {s.cta}
                    <ArrowUpRight size={14} />
                  </span>
                </button>
              </FadeUp>
            )
          )}
          <div className="border-t hairline" />
        </div>
      </div>
    </section>
  );
}

function DayToDuskBlock({ service: s }) {
  return (
    <FadeUp delay={0.05}>
      <div data-testid="service-day-to-dusk" className="border-t hairline py-12 md:py-16 px-2 md:px-4">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4">
            <span className="font-display text-2xl text-gold/80">{s.num}</span>
            <h3 className="mt-2 font-display font-light text-3xl md:text-4xl">{s.title}</h3>
            <p className="mt-4 font-display italic text-xl md:text-2xl text-bronze">
              From daylight to atmosphere.
            </p>
            <p className="mt-4 text-sm text-muted font-light leading-relaxed">{s.desc}</p>
            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2">
              {s.items.map((it) => (
                <li key={it} className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-ivory/60">
                  {it}
                </li>
              ))}
            </ul>
            <button
              data-testid="day-to-dusk-cta"
              onClick={() => lenisScroll("#contact")}
              className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-bronze hover:text-gold transition-colors duration-300"
            >
              {s.cta}
              <ArrowUpRight size={14} />
            </button>
          </div>
          <div className="lg:col-span-8">
            <CompareSlider
              before={IMG.dayToDuskBefore}
              after={IMG.dayToDuskAfter}
              ratio="aspect-[3/2]"
            />
            <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-muted">
              Client project — daylight capture transformed to twilight
            </p>
          </div>
        </div>
      </div>
    </FadeUp>
  );
}
